// ===========================================
// All Notes Modal Component
// ===========================================

import { useState, useMemo, useEffect } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Typography,
} from '@mui/material';
import {
  Close as CloseIcon,
  Description as DescriptionIcon,
} from '@mui/icons-material';
import { Note } from '../../../api/notes.api';
import { Term } from '../../../api/admin.api';
import { filterableTerms, noteInTerm, noteYear, termYear } from '../../../utils/noteTerms';
import NoteItem from './NoteItem';

interface Props {
  open: boolean;
  onClose: () => void;
  notes: Note[];
  studentName: string;
  terms: Term[];
}

/**
 * All Notes Modal with year/term filter
 */
export default function AllNotesModal({ open, onClose, notes, studentName, terms: allTerms }: Props) {
  // A note is filed by its session day, never its createdAt instant (utils/noteTerms.ts, #272).
  const terms = useMemo(() => filterableTerms(allTerms), [allTerms]);

  // Get unique years from notes and terms
  const years = useMemo(() => {
    const noteYears = notes.map(noteYear);
    const termYears = terms.map(termYear);
    const allYears = [...new Set([...noteYears, ...termYears])];
    return allYears.sort((a, b) => b - a); // Most recent first
  }, [notes, terms]);

  const [selectedYear, setSelectedYear] = useState<number | 'all'>('all');
  const [selectedTermId, setSelectedTermId] = useState<string | 'all'>('all');

  // Filter terms by selected year
  const availableTerms = useMemo(() => {
    if (selectedYear === 'all') return terms;
    return terms.filter((t) => termYear(t) === selectedYear);
  }, [terms, selectedYear]);

  // Reset term selection when year changes
  useEffect(() => {
    setSelectedTermId('all');
  }, [selectedYear]);

  // Filter notes
  const filteredNotes = useMemo(() => {
    let filtered = notes.filter(
      (n) => n.visibility === 'PARENTS' || n.visibility === 'STUDENTS'
    );

    // Filter by year
    if (selectedYear !== 'all') {
      filtered = filtered.filter((n) => noteYear(n) === selectedYear);
    }

    // Filter by term: the note's session day inside the term's days, both ends inclusive
    if (selectedTermId !== 'all') {
      const term = terms.find((t) => t.id === selectedTermId);
      if (term) {
        filtered = filtered.filter((n) => noteInTerm(n, term));
      }
    }

    return filtered.sort(
      (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    );
  }, [notes, selectedYear, selectedTermId, terms]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box
          sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
        >
          <Typography variant="h6">
            <DescriptionIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
            {studentName}'s Class Notes
          </Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        {/* Filters */}
        <Stack direction="row" spacing={2} sx={{ mb: 3, mt: 1 }}>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Year</InputLabel>
            <Select
              value={selectedYear}
              label="Year"
              onChange={(e) => setSelectedYear(e.target.value as number | 'all')}
            >
              <MenuItem value="all">All Years</MenuItem>
              {years.map((year) => (
                <MenuItem key={year} value={year}>
                  {year}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Term</InputLabel>
            <Select
              value={selectedTermId}
              label="Term"
              onChange={(e) => setSelectedTermId(e.target.value)}
            >
              <MenuItem value="all">All Terms</MenuItem>
              {availableTerms.map((term) => (
                <MenuItem key={term.id} value={term.id}>
                  {term.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>

        {/* Notes list */}
        {filteredNotes.length === 0 ? (
          <Alert severity="info">No notes found for the selected filters.</Alert>
        ) : (
          <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
            {filteredNotes.map((note) => (
              <NoteItem key={note.id} note={note} showFullContent />
            ))}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Typography variant="caption" color="text.secondary" sx={{ flex: 1, pl: 2 }}>
          {filteredNotes.length} note{filteredNotes.length !== 1 ? 's' : ''}
        </Typography>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
